/**
 * Converts a hex color to RGB values
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const cleanHex = hex.replace('#', '');
  return {
    r: Number.parseInt(cleanHex.substring(0, 2), 16),
    g: Number.parseInt(cleanHex.substring(2, 4), 16),
    b: Number.parseInt(cleanHex.substring(4, 6), 16),
  };
};

/**
 * Converts RGB values to hex color
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Converts a CSS color value (rgb, rgba, hex) to hex format
 * Returns undefined if the color format is not supported
 */
export const colorToHex = (color: string): string | undefined => {
  if (color.startsWith('#')) {
    return color;
  }

  const rgbRegex = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/;
  const rgbMatch = rgbRegex.exec(color);

  if (rgbMatch) {
    const r = Number.parseInt(rgbMatch[1], 10);
    const g = Number.parseInt(rgbMatch[2], 10);
    const b = Number.parseInt(rgbMatch[3], 10);
    return rgbToHex(r, g, b);
  }

  return undefined;
};

/**
 * Generates a lighter variant of a hex color
 * @param hexColor - The hex color to lighten (e.g., '#ff6b6b')
 * @param factor - The lightening factor (0-1), defaults to 0.3
 * @returns The lightened hex color
 */
export const lightenColor = (hexColor: string, factor = 0.3): string => {
  const { r, g, b } = hexToRgb(hexColor);

  const newR = Math.min(255, Math.round(r + (255 - r) * factor));
  const newG = Math.min(255, Math.round(g + (255 - g) * factor));
  const newB = Math.min(255, Math.round(b + (255 - b) * factor));

  return rgbToHex(newR, newG, newB);
};
